import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserSuggestionsQuery } from '../../hooks/useUserSuggestionsQuery';
import { useUserContext } from '../../context/user';
import useCleanPaginationURL from '../../hooks/useCleanPaginationURL';
import { usePaginationTextReplace } from '../../hooks/usePaginationTextReplace';
import { UserSuggestionResponse } from '../../types/api/userSuggestions';
import Segment from '../navigation/Segment/Segment';
import { usersTabsData } from '../navigation/Segment/Segment+Data';
import { Placeholder, createErrorPlaceholder } from '../basic';
import { DisplayUserSuggestionModal } from './components/DisplayUserSuggestionModal';
import { UserTableRow } from '../basic/UserTableRow';
import { renderTruncatedText } from '../../helpers/strings';
import RegularPageLayout from '../../layouts/RegularPageLayout';
import { pageSize } from '../../services/settings';
import { formatDateTime } from '../../helpers/dates';
import { FeedbackStatus, getFeedbackStatusColor, getFeedbackStatusLabel, statusFilterConfig } from '../../types/api/feedback';
import { userSuggestionsTableConfig } from './UserSuggestionsList+Table';
import { ActionList, ActionMenu, Button, IconButton, Stack, Text } from '@primer/react'
import { Table, DataTable } from '@primer/react/experimental'
import { BriefcaseIcon, FilterIcon, XCircleIcon } from '@primer/octicons-react';

export default function UserSuggestionsList() {
    const { profile } = useUserContext();

    const navigate = useNavigate();

    const [selectedStatus, setSelectedStatus] = useState<FeedbackStatus | undefined>(undefined);
    const [pageIndex, setPageIndex] = React.useState(1)
    const [selectedSuggestion, setSelectedSuggestion] = useState<UserSuggestionResponse | undefined>(undefined);
    const [openSuggestionModal, setOpenSuggestionModal] = React.useState(false)

    const {
        isLoading,
        data,
        refetch,
        isFetched,
        isError
    } = useUserSuggestionsQuery(
        "",
        pageIndex,
        pageSize,
        "-created_at",
        selectedStatus
    );

    useCleanPaginationURL();
    usePaginationTextReplace()

    useEffect(() => {
        refetch()
    }, [pageIndex]);

    const openDisplaySuggestionModal = (suggestion: UserSuggestionResponse) => {
        setSelectedSuggestion(suggestion)
        setOpenSuggestionModal(true)
    }

    const suggestionDismissed = () => {
        setSelectedSuggestion(undefined)
        setOpenSuggestionModal(false)
    }

    const suggestionsStatusChanged = () => {
        refetch()
    }

    const emptyListDescription = `В сервисе ещё нет предложений
от пользователей`;

    return (
        <RegularPageLayout
            header={
                <Stack justify="space-between" direction="horizontal">
                    <Segment items={usersTabsData(profile)} />
                    <Stack direction="horizontal">
                        <ActionMenu>
                            <ActionMenu.Anchor>
                                <IconButton icon={FilterIcon} aria-label="Фильтр" />
                            </ActionMenu.Anchor>
                            <ActionMenu.Overlay>
                                <ActionList
                                    selectionVariant="single"
                                    role="menu"
                                    aria-label="Item"
                                    className='w-[232px]'
                                >
                                    <ActionList.Item
                                        key="all"
                                        role="menuitemradio"
                                        selected={selectedStatus === undefined}
                                        aria-checked={selectedStatus === undefined}
                                        onSelect={() => {
                                            setSelectedStatus(undefined)
                                        }}
                                    >
                                        <Stack align="center" direction="horizontal" gap='condensed'>
                                            <BriefcaseIcon size={16} />
                                            Все обращения
                                        </Stack>
                                    </ActionList.Item>
                                    <ActionList.Divider className='block mt-2' />
                                    {Object.entries(statusFilterConfig).map(([status, config]) => {
                                        const Icon = config.icon;
                                        return (
                                            <ActionList.Item
                                                key={status}
                                                role="menuitemradio"
                                                selected={selectedStatus === status}
                                                aria-checked={selectedStatus === status}
                                                onSelect={() => setSelectedStatus(status as FeedbackStatus)}
                                            >
                                                <Stack align="center" direction="horizontal" gap='condensed'>
                                                    <Icon size={16} />
                                                    {config.label}
                                                </Stack>
                                            </ActionList.Item>
                                        );
                                    })}
                                </ActionList>
                            </ActionMenu.Overlay>
                        </ActionMenu>
                    </Stack>
                </Stack>
            }
            content={
                <Stack>
                    {selectedSuggestion && <DisplayUserSuggestionModal
                        isOpen={openSuggestionModal}
                        suggestion={selectedSuggestion}
                        onDidSuggestionStatusChanged={suggestionsStatusChanged}
                        onClose={suggestionDismissed}
                    />}

                    {isError && !isLoading && createErrorPlaceholder()}
                    {isLoading && <Table.Container>
                        <Table.Skeleton
                            aria-labelledby="users-loading"
                            rows={pageSize}
                            columns={userSuggestionsTableConfig().map((el) => {
                                return {
                                    header: el.header,
                                    id: el.field,
                                }
                            })}
                        />
                    </Table.Container>}
                    {isFetched && data?.list.length === 0 && (
                        <Placeholder
                            icon={<XCircleIcon size={24} className='opacity-40' />}
                            header='Предложения не найдены'
                            description={emptyListDescription}
                            button={undefined}
                        />
                    )}
                    {isFetched && data && data.list.length > 0 &&
                        <Table.Container>
                            <DataTable
                                aria-labelledby="users-table"
                                data={data.list}
                                columns={[
                                    {
                                        id: 'name',
                                        rowHeader: true,
                                        header: "Пользователь",
                                        renderCell: (row) => {
                                            const user = (row as UserSuggestionResponse).user;
                                            return <UserTableRow
                                                user={user}
                                                onClick={() => navigate('/users/user?id=' + user.id)} />
                                        }
                                    },
                                    {
                                        id: 'phone',
                                        header: "Номер телефона",
                                        renderCell: (row) => {
                                            const suggestion = row as UserSuggestionResponse;
                                            return (suggestion.user?.username === 'Unknown User' && suggestion.user.phone == null) ? '-' : suggestion.user.phone
                                        }
                                    },
                                    {
                                        id: 'email',
                                        header: "E-mail",
                                        renderCell: (row) => {
                                            const suggestion = row as UserSuggestionResponse;
                                            return renderTruncatedText(suggestion.user.email, 32)
                                        }
                                    },
                                    {
                                        id: 'suggestion',
                                        header: "Предложение",
                                        renderCell: (row) => {
                                            const suggestion = row as UserSuggestionResponse
                                            return (
                                                <Stack direction="horizontal" align="center">
                                                    <Stack gap='none'>
                                                        <Button
                                                            onClick={() => openDisplaySuggestionModal(suggestion)}
                                                            className='!h-[2px] -ml-2'
                                                            size="small"
                                                            variant="invisible">
                                                            <Text
                                                                as="span"
                                                                weight='semibold'
                                                                className='text-[14px] text-aloix-black'
                                                            >
                                                                {renderTruncatedText(suggestion.text, 32)}
                                                            </Text>
                                                        </Button>
                                                    </Stack>
                                                </Stack>
                                            )
                                        }
                                    },
                                    {
                                        id: 'address',
                                        header: 'Путь',
                                        renderCell: (row) => {
                                            const suggestion = row as UserSuggestionResponse;
                                            return suggestion.appScreen
                                        }
                                    },
                                    {
                                        id: 'date',
                                        header: 'Дата и время',
                                        renderCell: (row) => {
                                            const date = (row as UserSuggestionResponse).createdAt;
                                            return formatDateTime(date, ',')
                                        }
                                    },
                                    {
                                        id: 'status',
                                        header: 'Статус',
                                        renderCell: (row) => {
                                            const status = (row as UserSuggestionResponse).status;
                                            const color = getFeedbackStatusColor(status)
                                            return <p style={{ color: color }}>{getFeedbackStatusLabel(status)}</p>
                                        }
                                    },
                                ]}
                            />
                        </Table.Container>
                    }
                    {isFetched && data && data.list.length > 0 &&
                        (<div className="sticky bottom-0 bg-white border-t border-gray-200 z-10 -mt-4">
                            <Table.Pagination aria-label="pagination" pageSize={pageSize} totalCount={data.totalCount} onChange={({
                                pageIndex
                            }) => {
                                setPageIndex(pageIndex + 1);
                            }} defaultPageIndex={pageIndex - 1} />
                        </div>
                        )}
                </Stack>
            }
        ></RegularPageLayout>

    )
}